import React, { useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, useGLTF, Html, useProgress } from '@react-three/drei';
import * as THREE from 'three';

function Model({ hidden, hiddenMeshes }) {
  const { scene } = useGLTF('/AirTent_V3.glb');

  React.useEffect(() => {
    const meshesToAnimate = [];
    scene.traverse((child) => {
      if (child.isMesh) {
        // Set material to double-sided to render both sides
        if (child.material) {
          child.material.side = THREE.DoubleSide;
          child.material.transparent = true; // Ensure transparency
          child.material.needsUpdate = true; // Force material update
        }

        // Special handling for Low_InnerPocket_1
        if (child.name === 'Low_InnerPocket_1') {
          console.log('Ensuring visibility for Low_InnerPocket_1');

          // Enhanced material settings for better visibility
          if (child.material) {
            child.material.side = THREE.DoubleSide;
            child.material.transparent = true;
            child.material.opacity = 1;
            child.material.depthWrite = true;
            child.material.depthTest = true;
            child.material.needsUpdate = true;
          }

          // Fix geometry for visibility
          const geometry = child.geometry;
          if (geometry) {
            geometry.computeBoundingSphere();
            geometry.computeBoundingBox();
            geometry.computeVertexNormals();
            geometry.attributes.position.needsUpdate = true;
            geometry.normalsNeedUpdate = true;
            child.visible = true;
          }

          // Ensure proper rendering order
          child.renderOrder = 1;
        }

        // Add mesh to the list for animation if it's in the hiddenMeshes array
        if (hiddenMeshes.includes(child.name)) {
          meshesToAnimate.push(child);
        }
      }
    });

    // Function to animate opacity
    const animateOpacity = () => {
      meshesToAnimate.forEach((mesh) => {
        if (hidden) {
          mesh.material.opacity = Math.max(mesh.material.opacity - 0.05, 0); // Gradually decrease opacity
          if (mesh.material.opacity === 0) mesh.visible = false; // Hide mesh when fully transparent
        } else {
          mesh.visible = true; // Ensure visibility before fading in
          mesh.material.opacity = Math.min(mesh.material.opacity + 0.05, 1); // Gradually increase opacity
        }
      });

      // Continue the animation if needed
      if (
        meshesToAnimate.some(
          (mesh) =>
            (hidden && mesh.material.opacity > 0) || (!hidden && mesh.material.opacity < 1)
        )
      ) {
        requestAnimationFrame(animateOpacity);
      }
    };

    animateOpacity();
  }, [hidden, hiddenMeshes, scene]);

  return <primitive object={scene} position={[0, 0, 0]} scale={[0.01, 0.01, 0.01]} />;
}

function Configurator() {
  const [hidden, setHidden] = useState(false); // Track whether meshes are hidden or visible
  const [loading, setLoading] = useState(true); // Track the loading state
  const hiddenMeshes = [
    'Low_Cover_base_1',
    'Low_Curtern_Back_1',
    'Low_Curtain_Front_1',
    'Low_Tent_Hood_Cover_Front_1',
    'Low_Cover_Post_1',
    'Low_Hood_Stick_1',
    'Low_Tent_Cover_Hood_1',
    'Low_Tent_Hood_Cover_Sides_1',
  ]; // Correct mesh names from the debug output

  const toggleVisibility = () => setHidden((prev) => !prev);

  const { progress } = useProgress(); // Track loading progress
  React.useEffect(() => {
    if (progress === 100) {
      setLoading(false); // Hide the loading bar when progress reaches 100
    }
  }, [progress]);

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        width: '100vw',
        background: '#ffffff', // Solid white background
        overflow: 'hidden',
      }}
    >
      {loading && <LoadingBar />}
      <Canvas
        style={{ flex: 1 }}
        camera={{ position: [25, 25, 25], fov: 75 }}
        onCreated={({ gl }) => {
          gl.setClearColor(new THREE.Color('#ffffff')); // White canvas background
        }}
      >
        <ambientLight intensity={2.8} />
        <directionalLight position={[5, 5, 5]} intensity={1.5} />
        <Model hidden={hidden} hiddenMeshes={hiddenMeshes} />
        <OrbitControls enableZoom={true} enablePan={false} makeDefault />
        <Html
          position={[0, 10.5, 15.5]}
          distanceFactor={30}
          occlude
          onOcclude={(isOccluded) => {
            const element = document.querySelector('.annotation');
            if (element) {
              element.style.opacity = isOccluded ? '0.3' : '1';
            }
          }}
        >
          <div
            className="annotation"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              position: 'relative',
              left: '-50%', // Center-left positioning
              transform: 'translateY(-50%)',
              padding: '10px',
              opacity: '1', // Default opacity
              transition: 'opacity 0.3s ease', // Smooth opacity transition
            }}
          >
            {/* Dot */}
            <div
              style={{
                width: '15px',
                height: '15px',
                backgroundColor: '#ffffff', // Blue dot color
                borderRadius: '50%', // Ensures the dot is a perfect circle
                marginRight: '15px',
              }}
            ></div>
            {/* Button */}
            <button
              onClick={toggleVisibility}
              style={{
                backgroundColor: '#ffffff',
                color: 'black',
                border: '1px solid #ccc',
                borderRadius: '5px',
                padding: '10px 20px', // Adjust padding for better spacing
                cursor: 'pointer',
                fontSize: '16px',
                fontFamily: 'Helvetica, Arial, sans-serif', // Sets font to Helvetica with fallback options
                width: '160px', // Set a fixed width to accommodate text in one line
                textAlign: 'center',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', // Subtle shadow for the button
              }}
            >
              {hidden ? 'Hide Inner Tent' : 'Show Inner Tent'}
            </button>
          </div>
        </Html>
      </Canvas>
    </div>
  );
}

function LoadingBar() {
  const { progress } = useProgress(); // Get progress from useProgress

  return (
    <div
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        color: '#fff',
        padding: '20px',
        borderRadius: '5px',
        zIndex: 10,
        textAlign: 'center',
      }}
    >
      <p>Loading... {Math.round(progress)}%</p>
      <div
        style={{
          width: '200px',
          height: '10px',
          backgroundColor: '#ccc',
          borderRadius: '5px',
          overflow: 'hidden',
          marginTop: '10px',
        }}
      >
        <div
          style={{
            width: `${progress}%`,
            height: '100%',
            backgroundColor: '#007BFF',
          }}
        ></div>
      </div>
    </div>
  );
}

export default Configurator;
